/**
 * The captain's pacing model — who speaks, when, and about what.
 *
 * This is the port of `PORT-SPEC.md` §2's three-layer selection model onto lumen's
 * buckets, and it holds no DOM, no timers and no wall clock of its own. The caller
 * hands it a millisecond stamp and a three-field sample of the world; it hands back a
 * line or `null`. That split is what makes the pacing testable in `node` — a run's
 * worth of speech can be simulated in a loop without a browser (`tests/captain-speech.test.ts`).
 *
 * **The three layers, unchanged in shape from the source:**
 *
 *  1. *Bucket-change-only emission.* A bucket that stays true for eight seconds
 *     produces one line, not four hundred and eighty. This is the pacing; the
 *     cooldowns are the safety net behind it.
 *  2. *The chattiness gate.* Reactive buckets (`hurt`, `close`, `boss`, `bossdown`)
 *     bypass the ambient gap and obey the shorter reactive one. Everything else waits
 *     out `CADENCE.earlyCooldownMs` inside the attachment window and
 *     `CADENCE.ambientCooldownMs` after it. `quiet` and `deep` also take a coin flip,
 *     so a lull does not reliably produce chatter.
 *  3. *The pick.* Delegated whole to `pickLine` / `pickCallback`, which carry the
 *     depth-1 per-bucket anti-repeat and the once-only ledger.
 *
 * **What lumen adds over the source.** The source has no `bored` re-arm problem
 * because its moods flip constantly; lumen's `early` bucket stays true for two solid
 * minutes, and bucket-change-only emission would spend that window on one line. So a
 * bucket that is a *state* rather than an *event* re-offers a slot every `repeatMs`.
 * Reactive buckets keep entry-only emission, which is why a hit storm produces one
 * "we're hit" and not eleven.
 *
 * **Determinism.** The stream is `(runSeed ^ fnv1a32('captain.voice')) >>> 0` — the
 * same Appendix A.1 derivation the face and the name use, on a name that is not a
 * member of `STREAM_NAMES`. The same captain in the same run draws the same lines in
 * the same order. Frame timing still decides *when* `poll` is called, so this is
 * same-flavour rather than frame-exact, which is the most a presentation layer driven
 * by rAF can honestly promise.
 *
 * **Permadeath.** Nothing here reads or writes storage, and the object is dropped
 * whole when the run ends. `src/captain/**` is lint-banned from `localStorage` and
 * `sessionStorage` (`tests/captain-seed.test.ts`); the integration layer holds the
 * same line by owning no persistence at all.
 */

import {
  CADENCE,
  makeLineState,
  pickCallback,
  pickLine,
  spellMinutes,
  takeDeathLine,
  type Bucket,
  type LineState,
  type RunMemory,
} from '../captain/lines';
import { voiceKey, type Captain, type VoiceKey } from '../captain/seed';
import { RngStream, fnv1a32 } from '../core/rng';

/** What came out of the voice, and which channel produced it. */
export interface SpeechRequest {
  text: string;
  kind: Bucket | 'callback';
}

/**
 * The whole read the voice takes off `World`. Three scalars, no pool walk, no spatial
 * query: the UI reads this once per rendered frame and the sim never learns it did.
 */
export interface VoiceSample {
  runTimeS: number;
  /** `player.hp / player.max_hp`, clamped by the caller. */
  hpFrac: number;
  enemiesAlive: number;
}

/** A beat after the run opens, so the first line lands on a moving ship. */
export const BOARDING_DELAY_MS = 1800;

/** Below this the captain is talking about dying, not about the boss. */
export const CLOSE_HP_FRAC = 0.3;

/** Bodies on the field that read as "a lot of them, close". */
export const PRESSURE_ENEMIES = 24;

/** `director.run_duration_s` is 1200; the halfway mark is where the run gets weight. */
export const DEEP_AT_S = 600;

/**
 * How long a reactive bucket stays the winner after its trigger. Long enough that the
 * gate below has a chance to let it through, short enough that a boss arrival is not
 * still being discussed when the boss dies.
 */
export const WINDOW_MS: Readonly<Record<'hurt' | 'boss' | 'bossdown' | 'boon' | 'wave', number>> = {
  hurt: 1800,
  boss: 2600,
  bossdown: 2600,
  boon: 2200,
  wave: 2200,
};

/** A state bucket that stays true re-offers a slot this often. */
export const STATE_REPEAT_MS = 20000;

/**
 * Ranked exactly as the source's `MOOD_PRIORITY` is, against lumen's buckets. `close`
 * outranks `boss` on purpose: a captain at 20% hull does not comment on the scenery.
 */
export const PRIORITY: Readonly<Record<Bucket, number>> = {
  boarding: 200,
  hurt: 100,
  close: 95,
  boss: 90,
  bossdown: 88,
  boon: 70,
  wave: 60,
  pressure: 50,
  early: 20,
  deep: 15,
  quiet: 10,
};

/** Bypass the ambient gap; obey `CADENCE.reactiveCooldownMs` instead. */
const HIGH_PRIO: ReadonlySet<Bucket> = new Set<Bucket>(['hurt', 'close', 'boss', 'bossdown']);

/** Fire on entry only. Everything else re-offers on a timer. */
const ENTRY_ONLY: ReadonlySet<Bucket> = new Set<Bucket>(['boarding', 'hurt', 'boss', 'bossdown', 'boon', 'wave']);

/** The two buckets that take the coin flip, mirroring the source's `bored`/`idle`. */
const FLIPPED: ReadonlySet<Bucket> = new Set<Bucket>(['quiet', 'deep']);

const NEVER = -1e12;

function repeatMs(bucket: Bucket): number {
  if (ENTRY_ONLY.has(bucket)) return Number.POSITIVE_INFINITY;
  if (bucket === 'close' || bucket === 'pressure') return STATE_REPEAT_MS;
  if (bucket === 'early') return CADENCE.earlyCooldownMs;
  return CADENCE.ambientCooldownMs;
}

export class CaptainVoice {
  readonly captain: Captain;
  readonly key: VoiceKey;
  /** What a `callback` line templates against. Written here, read by `pickCallback`. */
  readonly memory: RunMemory = {};

  private readonly rng: RngStream;
  private readonly state: LineState = makeLineState();

  private startedAt = 0;
  private boarded = false;
  private silenced = false;

  /** The winning bucket on the previous poll. `null` until the first one lands. */
  private bucket: Bucket | null = null;
  private lastShownAt = NEVER;
  private readonly lastSlotAt = new Map<Bucket, number>();

  private hurtUntil = NEVER;
  private bossUntil = NEVER;
  private bossDownUntil = NEVER;
  private boonUntil = NEVER;
  private waveUntil = NEVER;

  private worstHit = 0;
  private lastMinutes = -1;

  constructor(captain: Captain) {
    this.captain = captain;
    this.key = voiceKey(captain);
    this.rng = new RngStream((captain.runSeed ^ fnv1a32('captain.voice')) >>> 0);
  }

  /** Starts the boarding clock. Call once, when the run begins. */
  start(nowMs: number): void {
    this.startedAt = nowMs;
  }

  /** No more ambient lines. Called on `game_over` so nothing steps on the death beat. */
  silence(): void {
    this.silenced = true;
  }

  get isSilenced(): boolean {
    return this.silenced;
  }

  // --- what the run did to them --------------------------------------------

  noteHit(nowMs: number, amount: number, sourceName: string): void {
    this.hurtUntil = nowMs + WINDOW_MS.hurt;
    if (amount > this.worstHit && sourceName.length > 0) {
      this.worstHit = amount;
      this.memory.hitter = sourceName;
    }
  }

  noteBoss(nowMs: number, displayName: string): void {
    this.bossUntil = nowMs + WINDOW_MS.boss;
    if (this.memory.boss === undefined && displayName.length > 0) this.memory.boss = displayName;
  }

  noteBossDown(nowMs: number): void {
    this.bossDownUntil = nowMs + WINDOW_MS.bossdown;
  }

  noteBoon(nowMs: number, displayName: string): void {
    this.boonUntil = nowMs + WINDOW_MS.boon;
    if (this.memory.firstBoon === undefined && displayName.length > 0) this.memory.firstBoon = displayName;
  }

  noteWave(nowMs: number): void {
    this.waveUntil = nowMs + WINDOW_MS.wave;
  }

  // --- the poll -------------------------------------------------------------

  /**
   * One line or nothing. Cheap enough to call every rendered frame: the common path is
   * two comparisons and a map lookup, and it allocates only when it speaks.
   */
  poll(nowMs: number, sample: VoiceSample): SpeechRequest | null {
    if (this.silenced) return null;

    const minutes = Math.floor(sample.runTimeS / 60);
    if (minutes !== this.lastMinutes) {
      this.lastMinutes = minutes;
      this.memory.minutes = spellMinutes(minutes);
    }

    if (!this.boarded) {
      if (nowMs - this.startedAt < BOARDING_DELAY_MS) return null;
      this.boarded = true;
      const text = pickLine(this.key, 'boarding', this.rng, this.state);
      if (text !== null) {
        this.lastShownAt = nowMs;
        this.lastSlotAt.set('boarding', nowMs);
        this.bucket = 'boarding';
        return { text, kind: 'boarding' };
      }
    }

    const winner = this.derive(nowMs, sample);
    const changed = winner !== this.bucket;
    this.bucket = winner;

    const lastSlot = this.lastSlotAt.get(winner);
    const due = lastSlot === undefined || nowMs - lastSlot >= repeatMs(winner);
    if (!changed && !due) return null;

    const gap = HIGH_PRIO.has(winner)
      ? CADENCE.reactiveCooldownMs
      : sample.runTimeS < CADENCE.earlyWindowS
        ? CADENCE.earlyCooldownMs
        : CADENCE.ambientCooldownMs;
    if (nowMs - this.lastShownAt < gap) return null;

    // Past the gate, the slot is spent whether or not a line comes out of it. Without
    // this a failed coin flip would be retried on the very next frame and the flip
    // would mean nothing.
    this.lastSlotAt.set(winner, nowMs);
    if (FLIPPED.has(winner) && this.rng.next() < 0.5) return null;

    let text: string | null = null;
    let kind: Bucket | 'callback' = winner;
    if (FLIPPED.has(winner) && this.rng.next() < CADENCE.callbackShare) {
      const recalled = pickCallback(this.key, this.rng, this.state, this.memory);
      if (recalled !== null) {
        text = recalled;
        kind = 'callback';
      }
    }
    if (text === null) text = pickLine(this.key, winner, this.rng, this.state);
    if (text === null) return null;

    this.lastShownAt = nowMs;
    return { text, kind };
  }

  /** Highest-priority bucket that is currently true. Never returns `boarding`. */
  private derive(nowMs: number, sample: VoiceSample): Bucket {
    let best: Bucket = 'quiet';
    let bestP = PRIORITY.quiet;
    const consider = (b: Bucket): void => {
      const p = PRIORITY[b];
      if (p > bestP) {
        best = b;
        bestP = p;
      }
    };
    if (nowMs < this.hurtUntil) consider('hurt');
    if (sample.hpFrac < CLOSE_HP_FRAC) consider('close');
    if (nowMs < this.bossUntil) consider('boss');
    if (nowMs < this.bossDownUntil) consider('bossdown');
    if (nowMs < this.boonUntil) consider('boon');
    if (nowMs < this.waveUntil) consider('wave');
    if (sample.enemiesAlive >= PRESSURE_ENEMIES) consider('pressure');
    if (sample.runTimeS < CADENCE.earlyWindowS) consider('early');
    else if (sample.runTimeS >= DEEP_AT_S) consider('deep');
    return best;
  }

  /**
   * The last thing they say. Ranked best-first and consumed from a ledger the caller
   * owns, so the second captain of a session never gets the first captain's line.
   * `null` means say nothing, which is better than saying something weaker.
   */
  deathLine(session: LineState): string | null {
    return takeDeathLine(this.key, session);
  }
}

/**
 * The session's death-line ledger. Lives in memory for exactly as long as the tab
 * does, is never written anywhere, and is the one piece of state that outlives a
 * captain — so that losing a second captain does not replay the first one's exit.
 */
export function makeDeathLedger(): LineState {
  return makeLineState();
}
